// ✅ src/pages/Asset/AssetTaxType.jsx  (FULL FILE - API Integrated Save/Update + Load By Id)
// Backend mount:
//   app.use("/api/taxtypelisting", assettaxTypeListing);
//
// Endpoints used:
//   GET  /api/taxtypelisting/:id
//   POST /api/taxtypelisting/save
//   PUT  /api/taxtypelisting/update/:id
//
// Route usage:
//   /Asset/AssetTaxMasterEntry           -> create
//   /Asset/AssetTaxMasterEntry?id=12     -> edit

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/taxtypelisting";

const AssetTaxType = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage helper
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ read ?id= from query string
  const taxId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const id = parseInt(params.get("id") || "0", 10);
    return Number.isFinite(id) ? id : 0;
  }, [location.search]);

  const isEdit = !!taxId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    taxType: "",
    isActive: true,
  });

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const requiredClass = "text-red-500";

  // ✅ Load existing on edit
  const loadById = async (id) => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/${id}`, {
        headers: commonHeaders,
      });

      const data = res.data?.data;
      if (!data) {
        toast.error("Record not found");
        return;
      }

      setFormData({
        taxType: data.taxType ?? data.fld_TaxType ?? "",
        isActive: !!(data.isActive ?? data.fld_IsActive ?? true),
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load tax type");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) loadById(taxId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, taxId, commonHeaders]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    if (!formData.taxType.trim()) {
      toast.error("Tax Type is required!");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      if (isEdit) {
        const res = await axios.put(
          `${API_BASE}/update/${taxId}`,
          {
            taxType: formData.taxType,
            isActive: formData.isActive,
          },
          {
            headers: commonHeaders,
          }
        );

        toast.success(res.data?.message || "Updated Successfully");
      } else {
        const res = await axios.post(
          `${API_BASE}/save`,
          {
            taxType: formData.taxType,
            isActive: formData.isActive,
          },
          {
            headers: commonHeaders,
          }
        );

        toast.success(res.data?.message || "Saved Successfully");
      }

      navigate("/Asset/AssetTaxMasterListing");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Please Try Later");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET TAX TYPE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {isEdit ? `Asset Tax Type (Edit #${taxId})` : "Asset Tax Type (New)"}
              </h1>
            </div>

            <div className="flex items-end gap-3">
              <button
                onClick={handleBack}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? `${buttonClass} ${hoverClass} text-white`
                    : `${buttonClass} ${hoverClass} text-white`
                }`}
              >
                <FaArrowLeft />
                Back
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl border p-6 ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="max-w-2xl mx-auto space-y-6">
              {loading ? (
                <div className="py-10 text-center">Loading...</div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                    <label className={`${labelClass} text-right md:text-right`}>
                      Tax Type <span className={requiredClass}>*</span>
                    </label>
                    <input
                      type="text"
                      name="taxType"
                      value={formData.taxType}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Enter tax type"
                      maxLength={200}
                    />
                  </div>

                  <div className="border-t pt-6 mt-6 border-gray-200 dark:border-gray-700">
                    <div className="flex justify-center">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleChange}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                          IsActive
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-center pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        saving
                          ? "bg-gray-400 text-white cursor-not-allowed"
                          : isDark
                          ? "bg-blue-700 hover:bg-blue-600 text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                    >
                              <FaSave />
                      {saving ? (isEdit ? "Updating..." : "Saving...") : (isEdit ? "Update" : "Save")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTaxType;